import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import './ServiceEditor.css';
import { serviceService, productService, serviceCostService, staffService } from '../services';
import calculateServicePrice from '../utils/calculateServicePrice';
import { getBusinessSettings, saveBusinessSettings } from '../utils/businessSettingsHelper';
import { calculateSupportStaffCostPerMinute } from '../utils/supportStaffCostCalculator';

// ─── Helper functions ────────────────────────────────────────────────────────

const calculateOverheadDisplay = (businessSettings, service) => {
  if (!businessSettings?.overhead_per_minute || !service?.time) return '0.00';
  return (businessSettings.overhead_per_minute * service.time).toFixed(2);
};

const calculateStaffCostDisplay = (staff, selectedStaffId, service) => {
  if (!selectedStaffId || !staff?.length || !service?.time) return null;
  const selectedStaff = staff.find(s => s.id === selectedStaffId);
  if (!selectedStaff?.hourly_pay) return null;
  const timeInHours = service.time / 60;
  return {
    cost: (selectedStaff.hourly_pay * timeInHours).toFixed(2),
    rate: selectedStaff.hourly_pay,
    hours: timeInHours.toFixed(1)
  };
};

const getProfitLevel = (businessSettings) => {
  return businessSettings?.profit_level_1 || 'default';
};

// ─── FIX: Derive owner_profit_per_minute from available settings fields ──────
// The API stores monthly_profit_target (or monthly_profit) and working hours
// but doesn't always persist the calculated per-minute value. This derives it
// from whatever raw fields are available so the editor never hard-blocks.
const deriveOwnerProfitPerMinute = (settings) => {
  if (settings.owner_profit_per_minute && parseFloat(settings.owner_profit_per_minute) > 0) {
    return parseFloat(settings.owner_profit_per_minute);
  }

  // Try monthly_profit_target first, then monthly_profit as fallback
  const monthlyProfit = parseFloat(settings.monthly_profit_target || settings.monthly_profit) || 0;
  // total_working_hours is the weekly figure set in trading hours
  const weeklyHours  = parseFloat(settings.total_working_hours || settings.weekly_working_hours) || 0;
  const weeksPerYear = parseFloat(settings.weeks_per_year) || 0;

  if (monthlyProfit > 0 && weeklyHours > 0 && weeksPerYear > 0) {
    const annualMinutes = weeklyHours * 60 * weeksPerYear;
    return annualMinutes > 0 ? (monthlyProfit * 12) / annualMinutes : 0;
  }

  return 0;
};
// ─────────────────────────────────────────────────────────────────────────────

const ServiceEditor = () => {
  const { serviceId, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const effectiveServiceId = serviceId || id;
  const isNewService = location.pathname.includes('/new') || !effectiveServiceId || effectiveServiceId === 'new';

  const [loading, setLoading]                   = useState(true);
  const [saving, setSaving]                     = useState(false);
  const [error, setError]                       = useState(null);
  const [products, setProducts]                 = useState([]);
  const [businessSettings, setBusinessSettings] = useState({});
  const [service, setService]                   = useState({
    id: null, name: '', category: '', time: 60, currentPrice: 0, staffId: null
  });
  const [staff, setStaff]                       = useState([]);
  const [selectedStaffId, setSelectedStaffId]   = useState(null);
  const [staffFetchComplete, setStaffFetchComplete] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [totalProductCost, setTotalProductCost] = useState(0);
  const [calculationResults, setCalculationResults] = useState(null);

  // ─── Support staff debug helper ─────────────────────────────────────────────
  const debugSupportStaffCalculation = (staffList, weeksPerYear) => {
    console.group('Support Staff Calculation Debug');
    try {
      const supportStaff = staffList.filter(s =>
        s.staff_type === 'support' &&
        parseFloat(s.hourly_pay) > 0 &&
        parseFloat(s.available_hours) > 0
      );
      let totalWeeklyCost = 0;
      supportStaff.forEach(s => {
        const rate  = parseFloat(s.effective_hourly_rate || s.hourly_pay) || 0;
        const hours = parseFloat(s.available_hours) || 0;
        totalWeeklyCost += rate * hours;
      });
      const revenueStaff = staffList.filter(s =>
        s.staff_type === 'revenue' && parseFloat(s.available_hours) > 0
      );
      let totalWeeklyHours = 0;
      revenueStaff.forEach(s => { totalWeeklyHours += parseFloat(s.available_hours) || 0; });
      const annualMinutes = totalWeeklyHours * 60 * weeksPerYear;
      const perMinute = annualMinutes > 0 ? (totalWeeklyCost * weeksPerYear) / annualMinutes : 0;
      console.log('Support staff per minute:', perMinute.toFixed(4));
      return { supportStaffCount: supportStaff.length, totalWeeklyCost, totalRevenueHours: totalWeeklyHours, supportStaffPerMinute: perMinute };
    } catch (err) {
      console.error('Error in debug calculation:', err);
    } finally {
      console.groupEnd();
    }
  };

  // ─── Main data load ─────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // 1. Business settings
        let settings = {};
        try {
          settings = await getBusinessSettings();
          console.log('Business settings loaded:', settings);
        } catch (settingsError) {
          console.error('Failed to fetch business settings:', settingsError);
          setError('Unable to load business settings. Please configure your business settings before creating or editing services.');
          setLoading(false);
          return;
        }

        // ─── FIX: calculate owner_profit_per_minute rather than hard-blocking ──
        const derivedOwnerProfit = deriveOwnerProfitPerMinute(settings);
        if (derivedOwnerProfit > 0) {
          settings = { ...settings, owner_profit_per_minute: derivedOwnerProfit };
        }

        // Only hard-block on overhead_per_minute — that truly cannot be derived
        // without the overhead rows, which live on a separate page.
        const missingSettings = [];
        if (!settings.overhead_per_minute || parseFloat(settings.overhead_per_minute) === 0) {
          missingSettings.push('Overhead Per Minute');
        }
        if (!settings.owner_profit_per_minute || parseFloat(settings.owner_profit_per_minute) === 0) {
          missingSettings.push('Owner Profit Per Minute (set a monthly profit target in Business Settings)');
        }

        if (missingSettings.length > 0) {
          setError(`Please configure your business settings before proceeding. Missing values: ${missingSettings.join(', ')}`);
          setLoading(false);
          return;
        }
        // ─────────────────────────────────────────────────────────────────────

        setBusinessSettings(settings);

        // 2. Products
        const productsResponse = await productService.getAllProducts();
        setProducts(productsResponse.data || []);

        // 3. Staff
        try {
          const staffResponse = await staffService.getStaff();
          if (!staffResponse || !Array.isArray(staffResponse)) {
            setStaff([]);
          } else {
            setStaff(staffResponse);
            if (staffResponse.length > 0 && settings.weeks_per_year) {
              debugSupportStaffCalculation(staffResponse, settings.weeks_per_year);
              const supportStaffPerMinute = calculateSupportStaffCostPerMinute(
                staffResponse,
                settings.weeks_per_year
              );
              if (supportStaffPerMinute > 0) {
                setBusinessSettings(prev => ({ ...prev, support_staff_per_minute: supportStaffPerMinute }));
              }
            }
          }
          setStaffFetchComplete(true);
        } catch (staffError) {
          console.error('Error fetching staff:', staffError);
          setStaff([]);
          setStaffFetchComplete(true);
        }

        // 4. Service data (edit mode only)
        if (isNewService) {
          setLoading(false);
          return;
        }

        if (!effectiveServiceId) {
          setError('Invalid service ID. Please start over with a valid service.');
          setLoading(false);
          return;
        }

        try {
          const serviceResponse = await serviceService.getServiceWithProducts(effectiveServiceId);
          const serviceData = serviceResponse.data;
          const staffId = serviceData.staff_id || serviceData.staffId || null;

          setService({
            id: serviceData.id,
            name: serviceData.name,
            category: serviceData.category,
            time: serviceData.time || serviceData.duration || serviceData.service_time || 60,
            currentPrice: serviceData.current_price || serviceData.current_cost || serviceData.currentPrice || 0,
            staffId
          });

          if (staffId) setSelectedStaffId(parseInt(staffId, 10));

          const productSelections = [];
          // Services store product details directly in the details array
          // Match back to the products list by name to get the full product object
          if (serviceData.details && serviceData.details.length > 0) {
            serviceData.details.forEach(detail => {
              const product = productsResponse.data.find(p =>
                p.Product_Name === detail.product_name
              );
              if (product) {
                productSelections.push({ product, usage: detail.usage || 0 });
              } else {
                // Product not found in list — create a minimal object so it still displays
                productSelections.push({
                  product: {
                    Product_ID: null,
                    Product_Name: detail.product_name,
                    price: detail.price_inc_vat,
                    qty: detail.pack_qty
                  },
                  usage: detail.usage || 0
                });
              }
            });
          } else if (serviceData.product_ids && serviceData.product_usage) {
            for (const productId of serviceData.product_ids) {
              const usage   = serviceData.product_usage[productId] || 0;
              const product = productsResponse.data.find(p => p.Product_ID == productId);
              if (product) productSelections.push({ product, usage });
            }
          }
          setSelectedProducts(productSelections);

        } catch (serviceError) {
          console.error('Error fetching service:', serviceError);
          if (serviceError.response?.status === 404) {
            setError('Service not found. It may have been deleted.');
          } else {
            setError('Failed to load service data: ' + (serviceError.response?.data?.error || serviceError.message));
          }
        }

      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data: ' + (err.response?.data?.error || err.message));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [effectiveServiceId, isNewService]);

  // ─── Product cost total ─────────────────────────────────────────────────────
  useEffect(() => {
    let total = 0;
    selectedProducts.forEach(selected => {
      const product = selected.product;
      if (!product) return;
      const price   = parseFloat(product.price)  || 0;
      const packQty = parseFloat(product.qty)     || 1;
      const usage   = parseFloat(selected.usage)  || 0;
      total += (price / packQty) * usage;
    });
    setTotalProductCost(total);
  }, [selectedProducts]);

  // ─── Auto-recalculate on input changes ─────────────────────────────────────
  useEffect(() => {
    if (!loading && businessSettings.overhead_per_minute && businessSettings.owner_profit_per_minute) {
      updateCalculationResults();
    }
  }, [service.time, selectedStaffId, selectedProducts, businessSettings, loading]);

  // ─── Input handlers ─────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setService(prev => ({ ...prev, [name]: value }));
  };

  const handleStaffSelection = (e) => {
    const staffId = e.target.value !== '' ? parseInt(e.target.value, 10) : null;
    setSelectedStaffId(staffId);
    setService(prev => ({ ...prev, staffId }));
  };

  const handleProductSelection = (e, product) => {
    if (e.target.checked) {
      setSelectedProducts(prev => [...prev, { product, usage: 1 }]);
    } else {
      setSelectedProducts(prev => prev.filter(item => item.product.Product_ID !== product.Product_ID));
    }
  };

  const handleUsageChange = (productId, usage) => {
    setSelectedProducts(prev =>
      prev.map(item =>
        item.product.Product_ID === productId
          ? { ...item, usage: parseFloat(usage) || 0 }
          : item
      )
    );
  };

  // ─── Calculation ────────────────────────────────────────────────────────────
  const calculateServiceCosts = () => {
    const serviceObj = {
      id: service.id,
      name: service.name,
      time: parseFloat(service.time) || 0,
      currentPrice: parseFloat(service.currentPrice) || 0
    };

    const selectedStylist = staff.find(s => parseInt(s.id) === parseInt(selectedStaffId));

    try {
      const workingSettings = { ...businessSettings };

      // Re-derive owner profit in case it slipped through as 0
      if (!workingSettings.owner_profit_per_minute || parseFloat(workingSettings.owner_profit_per_minute) === 0) {
        workingSettings.owner_profit_per_minute = deriveOwnerProfitPerMinute(workingSettings);
      }

      // Re-derive support staff cost if missing
      if (!workingSettings.support_staff_per_minute || parseFloat(workingSettings.support_staff_per_minute) === 0) {
        if (staff.length > 0 && workingSettings.weeks_per_year) {
          workingSettings.support_staff_per_minute = calculateSupportStaffCostPerMinute(
            staff,
            parseFloat(workingSettings.weeks_per_year)
          );
        }
      }

      const result = calculateServicePrice(serviceObj, selectedProducts, workingSettings, selectedStylist);

      if (result.error) {
        console.error('Calculation error:', result.error);
        return null;
      }

      return {
        product_cost:       result.breakdown.product_cost,
        time_cost:          result.breakdown.stylist_cost,
        overhead_cost:      result.breakdown.overhead_cost,
        support_staff_cost: result.breakdown.support_staff_cost || 0,
        owner_profit:       result.breakdown.owner_profit || 0,
        total_cost:         result.breakdown.total_cost,
        recommended_price:  result.prices.level_1,
        profit_margin:      parseFloat(workingSettings.profit_level_1) || 0,
        vat_rate:           parseFloat(workingSettings.vat_rate) || 0,
        price_with_vat:     result.prices.level_1_with_vat || result.prices.level_1,
        vat_applied:        result.breakdown.vat_applied,
        staff_id:           selectedStaffId,
        calculation_method: 'target'
      };
    } catch (err) {
      console.error('Error in service cost calculation:', err);
      return null;
    }
  };

  const updateCalculationResults = () => {
    setCalculationResults(calculateServiceCosts());
  };

  // ─── Helpers ────────────────────────────────────────────────────────────────
  const calculateProductCost = (product, usage) => {
    if (!product) return 0;
    return ((parseFloat(product.price) || 0) / (parseFloat(product.qty) || 1)) * (parseFloat(usage) || 0);
  };

  const formatCurrency = (amount) => `£${parseFloat(amount).toFixed(2)}`;

  // ─── Save ───────────────────────────────────────────────────────────────────
  const saveService = async () => {
    if (!service.name) { alert('Please enter a service name.'); return; }
    if (!selectedStaffId) { alert('Please select a staff member for accurate cost calculations.'); return; }
    if (!businessSettings.overhead_per_minute || !businessSettings.owner_profit_per_minute) {
      alert('Business settings are incomplete. Please configure them first.');
      return;
    }

    try {
      setSaving(true);

      // Ensure support staff per minute is set before saving
      if (parseFloat(businessSettings.support_staff_per_minute || 0) === 0 && staff.length > 0 && businessSettings.weeks_per_year) {
        const supportStaffPerMinute = calculateSupportStaffCostPerMinute(staff, businessSettings.weeks_per_year);
        if (supportStaffPerMinute > 0) {
          setBusinessSettings(prev => ({ ...prev, support_staff_per_minute: supportStaffPerMinute }));
          try {
            await saveBusinessSettings({ ...businessSettings, support_staff_per_minute: supportStaffPerMinute });
          } catch (e) {
            console.error('Failed to save support staff value:', e);
          }
        }
      }

      let finalServiceId = effectiveServiceId;

      if (isNewService) {
        const createResponse = await serviceService.createService({
          name: service.name, category: service.category,
          time: service.time, duration: parseFloat(service.time) || 0,
          service_time: parseFloat(service.time) || 0,
          currentPrice: service.currentPrice, current_cost: parseFloat(service.currentPrice) || 0,
          staffId: service.staffId
        });
        finalServiceId = createResponse.data.Service_ID || createResponse.data.id;
      } else {
        await serviceService.updateService(effectiveServiceId, {
          name: service.name, category: service.category,
          time: service.time, duration: parseFloat(service.time) || 0,
          service_time: parseFloat(service.time) || 0,
          currentPrice: service.currentPrice, current_cost: parseFloat(service.currentPrice) || 0,
          staffId: service.staffId
        });
      }

      if (!finalServiceId) {
        alert('Error: No service ID returned from server');
        setSaving(false);
        return;
      }

      await serviceService.associateProducts(finalServiceId, selectedProducts);

      const costData = calculateServiceCosts();
      if (costData) {
        await serviceCostService.updateServiceCosts(finalServiceId, { ...costData, staff_id: selectedStaffId });
        alert(isNewService ? 'Service created successfully!' : 'Service updated successfully!');
      } else {
        alert('Warning: Service saved but cost calculation failed. Please check business settings.');
      }

      navigate('/calculator/services');
    } catch (err) {
      console.error('Error saving service:', err);
      alert('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ─── Render helpers ─────────────────────────────────────────────────────────
  const renderCostBreakdown = () => {
    if (!calculationResults) return null;
    return (
      <div className="cost-breakdown">
        <h3>Cost Breakdown</h3>
        <table>
          <tbody>
            <tr><td>Product Cost:</td><td>{formatCurrency(calculationResults.product_cost)}</td></tr>
            <tr><td>Staff Cost:</td><td>{formatCurrency(calculationResults.time_cost)}</td></tr>
            <tr><td>Support Staff Cost:</td><td>{formatCurrency(calculationResults.support_staff_cost || 0)}</td></tr>
            <tr><td>Overhead:</td><td>{formatCurrency(calculationResults.overhead_cost)}</td></tr>
            <tr><td>Owner Profit:</td><td>{formatCurrency(calculationResults.owner_profit)}</td></tr>
            <tr className="total-row"><td>Breakeven Price:</td><td>{formatCurrency(calculationResults.total_cost)}</td></tr>
            <tr>
              <td>Level 1 Price ({businessSettings.profit_level_1 || 0}% markup):</td>
              <td>{formatCurrency(calculationResults.recommended_price)}</td>
            </tr>
            {(businessSettings.is_vat_registered === true ||
              businessSettings.is_vat_registered === 'y' ||
              businessSettings.is_vat_registered === 1 ||
              businessSettings.vat_registered === true) && (
              <tr><td>Price with VAT:</td><td>{formatCurrency(calculationResults.price_with_vat)}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const overheadCost  = calculateOverheadDisplay(businessSettings, service);
  const staffCostData = calculateStaffCostDisplay(staff, selectedStaffId, service);
  const profitLevel   = getProfitLevel(businessSettings);

  // ─── Render ─────────────────────────────────────────────────────────────────
  if (loading) return <div className="loading">Loading service data...</div>;

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        {error.includes('business settings') && (
          <button className="btn-primary" onClick={() => navigate('/business-settings')}>
            Configure Business Settings
          </button>
        )}
        <button className="btn-secondary" onClick={() => navigate('/calculator/services')}>
          Return to Services
        </button>
      </div>
    );
  }

  return (
    <div className="service-editor-container">
      <h2>{isNewService ? 'Add New Service' : 'Edit Service'}</h2>

      {/* Pricing Guidance Panel */}
      <div className="service-editor-guidance">
        <h3>💡 Service Pricing Guidance</h3>
        <div className="guidance-grid">
          <div className="guidance-item materials">
            <span className="guidance-icon">🧪</span>
            <div className="guidance-content">
              <strong>Cost of Materials:</strong> Select all products used in this service
              <div className="guidance-tip">
                Include hair color, treatments, shampoo, conditioner, styling products, and any consumables. Set realistic usage amounts based on actual consumption.
              </div>
            </div>
          </div>
          <div className="guidance-item duration">
            <span className="guidance-icon">⏱️</span>
            <div className="guidance-content">
              <strong>Service Duration:</strong> Include consultation, service, and styling time
              <div className="guidance-tip">
                Add time for client consultation (5-15 mins), the actual service, drying/styling, and cleanup between clients. Be realistic about total chair time.
              </div>
            </div>
          </div>
          <div className="guidance-item profit">
            <span className="guidance-icon">📈</span>
            <div className="guidance-content">
              <strong>Your Profit Settings:</strong> Using {profitLevel}% standard margin
              <div className="guidance-tip">
                Your business settings apply {profitLevel}% standard profit automatically. This is industry-appropriate for sustainable growth.
              </div>
            </div>
          </div>
          <div className="guidance-item staff">
            <span className="guidance-icon">👤</span>
            <div className="guidance-content">
              <strong>Staff Selection:</strong> Choose the specific stylist for this service
              <div className="guidance-tip">
                Select the actual staff member who will perform this service. Their individual hourly rate will be used for precise labour cost calculations.
              </div>
            </div>
          </div>
        </div>
        <div className="guidance-summary">
          <div className="summary-item">
            <strong>📊 Your calculations will appear below</strong> — All costs, breakdowns, and recommended pricing will be shown in the Cost Breakdown section as you build your service.
          </div>
        </div>
      </div>

      <h3>Service Details</h3>

      <div className="service-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="category">Category:</label>
            <input type="text" id="category" name="category" value={service.category || ''} onChange={handleInputChange} />
          </div>
          <div className="form-group">
            <label htmlFor="name">Service Name:</label>
            <input type="text" id="name" name="name" value={service.name || ''} onChange={handleInputChange} placeholder="Enter service name" required />
          </div>
          <div className="form-group">
            <label htmlFor="time">Service Time (minutes):</label>
            <input type="number" id="time" name="time" value={service.time || 0} onChange={handleInputChange} min="1" required />
          </div>
          <div className="form-group">
            <label htmlFor="currentPrice">Current Price:</label>
            <input type="number" id="currentPrice" name="currentPrice" value={service.currentPrice || 0} onChange={handleInputChange} min="0" step="0.01" />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="staffSelect">Staff Member *:</label>
            <select id="staffSelect" value={String(selectedStaffId) || ''} onChange={handleStaffSelection} className="staff-select" required>
              <option value="">Please select a staff member</option>
              {staff.map(staffMember => (
                <option key={staffMember.id} value={String(staffMember.id)}>
                  {staffMember.name || staffMember.employee_name} - £{staffMember.hourly_pay || 0}/hr
                </option>
              ))}
            </select>
            <div className="staff-info">
              {selectedStaffId ? (
                <p>✅ Using {staff.find(s => s.id === selectedStaffId)?.name || 'selected staff'} hourly rate for accurate calculations.</p>
              ) : (
                <p className="warning">⚠️ Please select a staff member to calculate accurate labour costs.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Products Section */}
      <div className="products-section">
        <h3>Select Products</h3>
        <table className="products-table">
          <thead>
            <tr>
              <th>Product</th><th>Select</th><th>Pack Qty</th>
              <th>Price</th><th>Per Unit</th><th>Usage</th><th>Cost</th>
            </tr>
          </thead>
          <tbody>
            {products.map(product => {
              const isSelected   = selectedProducts.some(item => item.product.Product_ID === product.Product_ID);
              const selectedItem = selectedProducts.find(item => item.product.Product_ID === product.Product_ID);
              const perUnit      = product.qty ? (parseFloat(product.price) / parseFloat(product.qty)).toFixed(2) : 0;
              return (
                <tr key={product.Product_ID} className={isSelected ? 'selected-row' : ''}>
                  <td>{product.Product_Name}</td>
                  <td>
                    <input type="checkbox" checked={isSelected} onChange={(e) => handleProductSelection(e, product)} />
                  </td>
                  <td>{product.qty}</td>
                  <td>{formatCurrency(product.price)}</td>
                  <td>{formatCurrency(perUnit)}</td>
                  <td>
                    {isSelected && (
                      <input type="number" min="0" step="0.1" value={selectedItem?.usage || 0}
                        onChange={(e) => handleUsageChange(product.Product_ID, e.target.value)}
                        className="usage-input" />
                    )}
                  </td>
                  <td>{isSelected ? formatCurrency(calculateProductCost(product, selectedItem?.usage || 0)) : formatCurrency(0)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="6" className="text-right"><strong>Total Product Cost:</strong></td>
              <td>{formatCurrency(totalProductCost)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {renderCostBreakdown()}

      <div className="buttons-section">
        <button type="button" className="cancel-button" onClick={() => navigate('/calculator/services')}>Cancel</button>
        <button type="button" className="save-button" onClick={saveService} disabled={saving}>
          {saving ? 'Saving...' : isNewService ? 'Create Service' : 'Update Service'}
        </button>
      </div>
    </div>
  );
};

export default ServiceEditor;